/**
 * Auth module — manual token management with auto-refresh.
 *
 * Pattern adapted from adview_system/frontend/src/api/auth.js:
 *   - login / logout via Directus /auth/login and /auth/logout
 *   - tokens in localStorage under `wecam_token` / `wecam_refresh`
 *   - `authFetch()` wrapper handles 401 → refresh → retry once
 *   - concurrent refresh calls are deduplicated via a shared promise
 *   - `AuthExpiredError` lets callers distinguish auth expiry from other errors
 */
import { DIRECTUS_URL } from "./directus";

const TOKEN_KEY = "wecam_token";
const REFRESH_KEY = "wecam_refresh";

/** Restored from localStorage on module load. */
let accessToken: string | null = localStorage.getItem(TOKEN_KEY);
let refreshToken: string | null = localStorage.getItem(REFRESH_KEY);

/** Deduplicate concurrent refresh calls. */
let refreshPromise: Promise<string | null> | null = null;

/** Sentinel error — thrown by authFetch when refresh fails or no token. */
export class AuthExpiredError extends Error {
  constructor() {
    super("Session expired");
    this.name = "AuthExpiredError";
  }
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires: number;
}

export interface CurrentUser {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  status: string;
  role?: { id: string; name: string } | string | null;
}

// ==========================================================================
//   Token persistence
// ==========================================================================

function setTokens(access: string | null, refresh?: string | null) {
  accessToken = access;
  if (access) {
    localStorage.setItem(TOKEN_KEY, access);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
  if (refresh !== undefined) {
    refreshToken = refresh;
    if (refresh) {
      localStorage.setItem(REFRESH_KEY, refresh);
    } else {
      localStorage.removeItem(REFRESH_KEY);
    }
  }
}

function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function getToken(): string | null {
  return accessToken;
}

export function hasToken(): boolean {
  return !!accessToken;
}

// ==========================================================================
//   Login / logout / refresh
// ==========================================================================

export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch(`${DIRECTUS_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.errors?.[0]?.message || "Login failed");
  }
  const { data } = await res.json();
  setTokens(data.access_token, data.refresh_token);
  return data;
}

export async function logout(): Promise<void> {
  if (refreshToken) {
    try {
      await fetch(`${DIRECTUS_URL}/auth/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      /* best effort — network errors on logout don't block local cleanup */
    }
  }
  clearTokens();
}

/**
 * Refresh the access token using the stored refresh token.
 * Deduplicates concurrent calls so parallel requests don't all trigger refreshes.
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshToken) return null;
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${DIRECTUS_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken, mode: "json" }),
      });
      if (!res.ok) {
        console.warn("[Auth] Refresh failed:", res.status);
        clearTokens();
        return null;
      }
      const { data } = await res.json();
      setTokens(data.access_token, data.refresh_token);
      return data.access_token as string;
    } catch (e) {
      console.warn("[Auth] Refresh error:", e);
      clearTokens();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ==========================================================================
//   authFetch — the canonical authenticated fetch wrapper
// ==========================================================================

/**
 * Fetch wrapper with automatic 401 → refresh → retry.
 *
 * Usage:
 *   const res = await authFetch(`${DIRECTUS_URL}/items/sites`);
 *   if (!res.ok) throw new Error("fetch failed");
 *   const { data } = await res.json();
 */
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  if (!accessToken) throw new AuthExpiredError();

  const buildRequest = (token: string) => ({
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  });

  let res = await fetch(url, buildRequest(accessToken));

  if (res.status === 401 && refreshToken) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      res = await fetch(url, buildRequest(newToken));
    }
  }

  if (res.status === 401) {
    clearTokens();
    throw new AuthExpiredError();
  }

  return res;
}

/** Convenience: GET and parse JSON, returning `data` or null on error. */
export async function apiGet<T>(path: string): Promise<T | null> {
  const res = await authFetch(`${DIRECTUS_URL}${path}`);
  if (!res.ok) return null;
  const json = await res.json();
  return json.data as T;
}

/** Convenience: POST and parse JSON, throwing on non-2xx. */
export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await authFetch(`${DIRECTUS_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.errors?.[0]?.message || `POST ${path} failed`);
  }
  return (await res.json()).data as T;
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await authFetch(`${DIRECTUS_URL}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.errors?.[0]?.message || `PATCH ${path} failed`);
  }
  return (await res.json()).data as T;
}

export async function apiDelete(path: string): Promise<void> {
  const res = await authFetch(`${DIRECTUS_URL}${path}`, { method: "DELETE" });
  if (!res.ok && res.status !== 204) {
    throw new Error(`DELETE ${path} failed (${res.status})`);
  }
}

// ==========================================================================
//   Current user
// ==========================================================================

export async function fetchCurrentUser(): Promise<CurrentUser | null> {
  if (!accessToken) return null;
  try {
    const fields = "id,email,first_name,last_name,status,role.id,role.name";
    const res = await authFetch(`${DIRECTUS_URL}/users/me?fields=${fields}`);
    if (!res.ok) {
      clearTokens();
      return null;
    }
    const json = await res.json();
    return json.data as CurrentUser;
  } catch (e) {
    if (e instanceof AuthExpiredError) return null;
    clearTokens();
    return null;
  }
}
